import Agilite from 'agilite'
import store from '../../../../store'
import BillingEnums from '../../utils/enums'
import { logAuditEvent, AuditActions, AuditEntityTypes } from './auditTrail'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

/**
 * Get follow-up accounts with filtering and pagination
 * @param {Object} filter - MongoDB filter object
 * @param {number} page - Page number
 * @param {number} pageLimit - Items per page
 * @param {string} projection - Field projection
 * @param {Object} sort - Sort criteria
 * @returns {Promise} Follow-up accounts data
 */
export const getFollowUpAccounts = (filter = {}, page = 1, pageLimit = 10, projection = '', sort = {}) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        filter = {
          ...filter,
          entityRef: store.getState().core.entity._id
        }

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.FOLLOW_UP_ACCOUNTS,
          BillingEnums.routeKeys.READ,
          {
            filter: JSON.stringify(filter),
            page,
            pageLimit,
            projection,
            sort: JSON.stringify(sort)
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Get single follow-up account by ID
 * @param {string} accountId - Account ID
 * @returns {Promise} Account data
 */
export const getFollowUpAccountById = (accountId) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.FOLLOW_UP_ACCOUNTS,
          BillingEnums.routeKeys.FIND_BY_ID,
          {
            recordId: accountId
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Create new follow-up account
 * @param {Object} data - Account data
 * @returns {Promise} Created account
 */
export const createFollowUpAccount = (data) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        data = {
          ...data,
          entityRef: store.getState().core.entity._id,
          createdBy: store.getState().auth.agiliteUser?._id,
          createdAt: new Date().toISOString()
        }

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.FOLLOW_UP_ACCOUNTS,
          BillingEnums.routeKeys.CREATE,
          {
            data: JSON.stringify(data)
          }
        )

        // Log audit trail for account creation
        await logAuditEvent(
          AuditActions.CREATE,
          AuditEntityTypes.FOLLOW_UP_ACCOUNT,
          response.data._id,
          `Follow-up account created for ${data.patientName} (${data.billingId})`,
          {
            patientName: data.patientName,
            billingId: data.billingId,
            patientId: data.patientId,
            outstandingAmount: data.outstandingAmount,
            status: data.status,
            accountGroup: data.accountGroup
          }
        )

        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Update follow-up account
 * @param {string} accountId - Account ID
 * @param {Object} data - Updated data
 * @returns {Promise} Updated account
 */
export const updateFollowUpAccount = (accountId, data) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Add entity ref to ensure we're only updating within the current entity
        const filter = {
          _id: accountId,
          entityRef: store.getState().core.entity._id
        }

        data = {
          ...data,
          modifiedBy: store.getState().auth.agiliteUser?._id,
          modifiedAt: new Date().toISOString()
        }

        // First, let's try with the update route using a specific filter
        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.FOLLOW_UP_ACCOUNTS,
          BillingEnums.routeKeys.UPDATE,
          {
            filter: JSON.stringify(filter),
            data: JSON.stringify(data),
            multi: false // Ensure only one record is updated
          }
        )

        // If no records were updated, the account might not exist
        if (!response.data || (response.data.modifiedCount === 0 && response.data.matchedCount === 0)) {
          throw new Error('Account not found or update failed')
        }

        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Update follow-up account by ID (using findByIdAndUpdate)
 * @param {string} accountId - Account ID
 * @param {Object} data - Updated data
 * @returns {Promise} Updated account
 */
export const updateFollowUpAccountById = (accountId, data) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        data = {
          ...data,
          modifiedBy: store.getState().auth.agiliteUser?._id,
          modifiedAt: new Date().toISOString()
        }

        // Try using findByIdAndUpdate if available
        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.FOLLOW_UP_ACCOUNTS,
          'findByIdAndUpdate',
          {
            id: accountId,
            update: JSON.stringify({ $set: data }),
            options: JSON.stringify({ new: true, runValidators: true })
          }
        )

        // Log audit trail for account update
        await logAuditEvent(
          AuditActions.UPDATE,
          AuditEntityTypes.FOLLOW_UP_ACCOUNT,
          accountId,
          `Follow-up account updated for ${response.data?.patientName || 'patient'}`,
          {
            accountId,
            billingId: data.billingId || response.data?.billingId,
            updatedFields: Object.keys(data),
            newStatus: data.status,
            newOutstandingAmount: data.outstandingAmount,
            modifiedBy: data.modifiedBy
          }
        )

        resolve(response.data)
      } catch (e) {
        // If findByIdAndUpdate is not available, fall back to regular update
        console.log('findByIdAndUpdate not available, using regular update')
        const response = await updateFollowUpAccount(accountId, data)
        resolve(response)
      }
    })()
  })
}

/**
 * Bulk update follow-up accounts
 * @param {Object} filter - Filter criteria
 * @param {Object} updateData - Data to update
 * @returns {Promise} Update result
 */
export const bulkUpdateFollowUpAccounts = (filter, updateData) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        filter = {
          ...filter,
          entityRef: store.getState().core.entity._id
        }

        updateData = {
          ...updateData,
          modifiedBy: store.getState().auth.agiliteUser?._id,
          modifiedAt: new Date().toISOString()
        }

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.FOLLOW_UP_ACCOUNTS,
          BillingEnums.routeKeys.UPDATE_MANY,
          {
            filter: JSON.stringify(filter),
            data: JSON.stringify(updateData)
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Count follow-up accounts
 * @param {Object} filter - Filter criteria
 * @returns {Promise} Count
 */
export const countFollowUpAccounts = (filter = {}) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        filter = {
          ...filter,
          entityRef: store.getState().core.entity._id
        }

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.FOLLOW_UP_ACCOUNTS,
          BillingEnums.routeKeys.COUNT,
          {
            filter: JSON.stringify(filter)
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Aggregate follow-up accounts data
 * @param {Array} pipeline - MongoDB aggregation pipeline
 * @returns {Promise} Aggregation result
 */
export const aggregateFollowUpAccounts = (pipeline = []) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Add entity filter as first stage
        pipeline.unshift({
          $match: {
            entityRef: store.getState().core.entity._id
          }
        })

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.FOLLOW_UP_ACCOUNTS,
          BillingEnums.routeKeys.AGGREGATE,
          {
            pipeline: JSON.stringify(pipeline)
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Bulk create follow-up accounts
 * @param {Array} accounts - Array of follow-up account objects
 * @returns {Promise} Created accounts
 */
export const bulkCreateFollowUpAccounts = (accounts) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Add entity reference and created info to each account
        const accountsWithDefaults = accounts.map((account) => ({
          ...account,
          entityRef: store.getState().core.entity._id,
          createdBy: store.getState().auth.agiliteUser?._id,
          createdAt: new Date().toISOString(),
          modifiedBy: store.getState().auth.agiliteUser?._id,
          modifiedAt: new Date().toISOString()
        }))

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.FOLLOW_UP_ACCOUNTS,
          BillingEnums.routeKeys.INSERT_MANY,
          {
            data: JSON.stringify(accountsWithDefaults)
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Get follow-up statistics for dashboard
 * @returns {Promise} Statistics data
 */
export const getFollowUpStats = () => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const pipeline = [
          {
            $match: {
              entityRef: store.getState().core.entity._id
            }
          },
          {
            $facet: {
              totalStats: [
                {
                  $group: {
                    _id: null,
                    totalOutstanding: { $sum: '$outstandingAmount' },
                    totalAccounts: { $sum: 1 },
                    overdueAccounts: {
                      $sum: {
                        $cond: [{ $eq: ['$agingBucket', '90+'] }, 1, 0]
                      }
                    }
                  }
                }
              ],
              statusBreakdown: [
                {
                  $group: {
                    _id: '$status',
                    count: { $sum: 1 },
                    amount: { $sum: '$outstandingAmount' }
                  }
                }
              ],
              agingBreakdown: [
                {
                  $group: {
                    _id: '$agingBucket',
                    count: { $sum: 1 },
                    amount: { $sum: '$outstandingAmount' }
                  }
                }
              ]
            }
          }
        ]

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.FOLLOW_UP_ACCOUNTS,
          BillingEnums.routeKeys.AGGREGATE,
          {
            pipeline: JSON.stringify(pipeline)
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Delete follow-up account
 * @param {string} accountId - Account ID
 * @returns {Promise} Deletion result
 */
export const deleteFollowUpAccount = (accountId) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.FOLLOW_UP_ACCOUNTS,
          BillingEnums.routeKeys.DELETE,
          {
            recordId: accountId
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Update account status with validation
 * @param {string} accountId - Account ID
 * @param {string} newStatus - New status
 * @param {string} notes - Status change notes
 * @returns {Promise} Updated account
 */
export const updateAccountStatus = (accountId, newStatus, notes = '') => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // First get the current account
        const currentAccount = await getFollowUpAccountById(accountId)

        if (!currentAccount) {
          throw new Error(BillingEnums.errorMessages.ACCOUNT_NOT_FOUND)
        }

        // Update status with audit trail
        const updateData = {
          status: newStatus,
          statusChangedAt: new Date().toISOString(),
          statusChangedBy: store.getState().auth.agiliteUser?._id,
          previousStatus: currentAccount.status
        }

        // Add to status history
        if (!currentAccount.statusHistory) {
          currentAccount.statusHistory = []
        }

        currentAccount.statusHistory.push({
          status: newStatus,
          previousStatus: currentAccount.status,
          changedAt: new Date().toISOString(),
          changedBy: store.getState().auth.agiliteUser?._id,
          notes
        })

        updateData.statusHistory = currentAccount.statusHistory

        const response = await updateFollowUpAccountById(accountId, updateData)
        resolve(response)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Record follow-up activity
 * @param {string} accountId - Account ID
 * @param {Object} activity - Activity data
 * @returns {Promise} Updated account
 */
export const recordFollowUpActivity = (accountId, activity) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Get current account
        const account = await getFollowUpAccountById(accountId)

        if (!account) {
          throw new Error(BillingEnums.errorMessages.ACCOUNT_NOT_FOUND)
        }

        // Add activity to history
        if (!account.followUpHistory) {
          account.followUpHistory = []
        }

        account.followUpHistory.unshift({
          ...activity,
          date: new Date().toISOString(),
          user: store.getState().auth.agiliteUser?.name || 'System',
          userId: store.getState().auth.agiliteUser?._id
        })

        // Update last contact date
        const updateData = {
          followUpHistory: account.followUpHistory,
          lastContactDate: new Date().toISOString()
        }

        // Update next follow-up date if provided
        if (activity.nextFollowUpDate) {
          updateData.nextFollowUpDate = activity.nextFollowUpDate
        }

        const response = await updateFollowUpAccount(accountId, updateData)
        resolve(response)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Delete all follow-up accounts (with confirmation)
 * @returns {Promise} Deletion result
 */
export const deleteAllFollowUpAccounts = () => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Delete ALL follow-up accounts without any filter
        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.FOLLOW_UP_ACCOUNTS,
          BillingEnums.routeKeys.DELETE_MANY,
          {
            filter: JSON.stringify({})
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}
